import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { Button } from "../../base/button/button";
import { Dialog, DialogContent, DialogTitle } from "../../base/dialog/dialog";
const MIN_SCALE = 0.5;
const MAX_SCALE = 6;
const SCALE_STEP = 0.25;
/** Wheel deltas differ wildly by device; scaling by a ratio keeps every one of them smooth. */ const WHEEL_SENSITIVITY = 0.0015;
function clampScale(scale) {
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}
/**
 * The full-window view of a diagram, because a flowchart with twenty nodes is
 * unreadable at transcript width. Zoom and pan are done here with a transform
 * rather than by handing the SVG to a graph library: the markup is already
 * drawn and sanitized, so the only thing missing is a bigger surface.
 */ export function MermaidDiagramViewer({ svg, open, onOpenChange }) {
    const { t } = useTranslation();
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({
        x: 0,
        y: 0
    });
    const drag = useRef(null);
    const reset = useCallback(()=>{
        setScale(1);
        setOffset({
            x: 0,
            y: 0
        });
    }, []);
    // Reset per opening: a diagram reopened at someone else's zoom reads as broken.
    const handleOpenChange = useCallback((next)=>{
        if (!next) reset();
        onOpenChange(next);
    }, [
        onOpenChange,
        reset
    ]);
    const handleWheel = (event)=>{
        setScale((current)=>clampScale(current * (1 - event.deltaY * WHEEL_SENSITIVITY)));
    };
    const handlePointerDown = (event)=>{
        event.currentTarget.setPointerCapture(event.pointerId);
        drag.current = {
            pointerX: event.clientX,
            pointerY: event.clientY,
            ...offset
        };
    };
    const handlePointerMove = (event)=>{
        const start = drag.current;
        if (!start) return;
        setOffset({
            x: start.x + (event.clientX - start.pointerX),
            y: start.y + (event.clientY - start.pointerY)
        });
    };
    const handlePointerUp = (event)=>{
        event.currentTarget.releasePointerCapture(event.pointerId);
        drag.current = null;
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: handleOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            className: "h-[90vh] w-[95vw] max-w-none gap-0 p-0 sm:max-w-none",
            children: [
                /*#__PURE__*/ _jsx(DialogTitle, {
                    className: "sr-only",
                    children: t("chat.diagram.label")
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "absolute right-14 top-3 z-10 flex gap-1",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            type: "button",
                            variant: "outline",
                            size: "icon",
                            "aria-label": t("chat.diagram.zoomOut"),
                            onClick: ()=>setScale((current)=>clampScale(current - SCALE_STEP)),
                            children: /*#__PURE__*/ _jsx(Minus, {
                                className: "size-4"
                            })
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            type: "button",
                            variant: "outline",
                            size: "icon",
                            "aria-label": t("chat.diagram.zoomIn"),
                            onClick: ()=>setScale((current)=>clampScale(current + SCALE_STEP)),
                            children: /*#__PURE__*/ _jsx(Plus, {
                                className: "size-4"
                            })
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            type: "button",
                            variant: "outline",
                            size: "icon",
                            "aria-label": t("chat.diagram.resetZoom"),
                            onClick: reset,
                            children: /*#__PURE__*/ _jsx(RotateCcw, {
                                className: "size-4"
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "size-full cursor-grab overflow-hidden active:cursor-grabbing",
                    onWheel: handleWheel,
                    onPointerDown: handlePointerDown,
                    onPointerMove: handlePointerMove,
                    onPointerUp: handlePointerUp,
                    onPointerCancel: handlePointerUp,
                    children: /*#__PURE__*/ _jsx("div", {
                        role: "img",
                        "aria-label": t("chat.diagram.label"),
                        className: "flex size-full items-center justify-center [&_svg]:max-h-none [&_svg]:max-w-none",
                        style: {
                            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                            // No transition: a transform that lags the pointer feels like a drop.
                            transformOrigin: "center"
                        },
                        /* Already through DOMPurify's SVG profile (plus the anchor/href
               FORBID list) where it was drawn — see SANITIZE_CONFIG in
               mermaid-diagram.tsx. Semgrep OSS cannot follow that cross-file
               taint, so the sink is marked, not the sanitization skipped. */ // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                        dangerouslySetInnerHTML: {
                            __html: svg
                        }
                    })
                })
            ]
        })
    });
}

//# sourceMappingURL=mermaid-diagram-viewer.js.map